import { differenceInCalendarDays, format, parseISO } from 'date-fns'

import { TIMEZONES } from '~types'

export function getDaysAgo(dateTime: Date | string): number {
  const parsedDate = typeof dateTime === 'string' ? parseISO(dateTime) : dateTime
  const today = new Date()

  return differenceInCalendarDays(today, parsedDate)
}

export function formatDaysAgo(daysAgo: number, dateTime?: Date | string): string {
  if (daysAgo === 0 && dateTime) {
    return formatTimeOnly(dateTime)
  } else if (daysAgo === 0) {
    return 'Today'
  } else if (daysAgo === 1) {
    return 'Yesterday'
  } else if (daysAgo === -1) {
    return 'Tomorrow'
  } else if (daysAgo > 1) {
    return `${daysAgo} days ago`
  } else {
    return `In ${Math.abs(daysAgo)} days`
  }
}

export function formatCSVDate(date: Date): string {
  // eg: 09/06/2023 11:29:13 PM
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }
  return date.toLocaleString('en-US', options).replace(',', '')
}

export function formatPaymentDate(date: Date | null): string {
  // Return "N/A" for null dates or Unix epoch time (1970-01-01)
  if (!date || date.getTime() === 0 || date.getFullYear() < 2000) {
    return 'N/A'
  }

  // eg: 09/06/23 11:29 PM
  const options: Intl.DateTimeFormatOptions = {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }
  return date.toLocaleString('en-US', options).replace(',', '')
}

export function formatOnlyDate(date: Date): string {
  // eg: 09/06/2023
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }
  return date.toLocaleString('en-US', options).replace(',', '')
}

export function formatWrittenDate(date: Date): string {
  // eg: Sep 6, 2023
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }
  return date.toLocaleString('en-US', options)
}

export function formatLastAction(date: Date): string {
  // eg: Just now / 5 minutes ago / Yesterday / 3 days ago / Sep 6 / Sep 6, 2023
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const calendarDays = differenceInCalendarDays(now, date)

  if (calendarDays <= 1) {
    if (diffSeconds < 60) return 'Just now'
    if (diffMinutes < 60) return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`
    return calendarDays == 0 ? 'Today' : 'Yesterday'
  }

  if (calendarDays < 7) return `${calendarDays} days ago`

  if (date.getFullYear() === now.getFullYear()) return format(date, 'MMM d')

  return format(date, 'MMM d, yyyy')
}

export function getDefaultCaseName(created?: string): string {
  const createdDate = created ? new Date(created) : undefined
  return createdDate && !Number.isNaN(createdDate.getTime())
    ? formatWrittenDate(createdDate)
    : 'Untitled Case'
}

export function formatWrittenDateTime(date: Date): string {
  // eg: Sep 6, 2023, 11:29 PM
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  }
  return date.toLocaleString('en-US', options)
}

export function formatTimeDay(date: Date): string {
  // eg: 11:29 PM, Sep 6
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  }
  return date.toLocaleString('en-US', options)
}

function formatTime12Hour(date: Date): string {
  const hours = date.getHours()
  const minutes = date.getMinutes()
  const ampm = hours >= 12 ? 'pm' : 'am'
  const formattedHours = hours % 12 || 12
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes
  return `${formattedHours}:${formattedMinutes} ${ampm}`
}

export function formatTimeOnly(dateTime: Date | string): string {
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime
  if (isNaN(date.getTime())) {
    return 'Invalid Date'
  }
  return formatTime12Hour(date)
}

export function formatDateNoSlashes(date: Date): string {
  // eg: 09062023
  return date
    .toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })
    .replace(/\//g, '')
}

export function formatDateNoSlashesReverse(date: Date): string {
  // eg: 20230609
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}${month}${day}`
}

export function formatStepStatusDate(dateString: string): string {
  const date = new Date(dateString)

  if (isNaN(date.getTime())) {
    return 'Invalid Date'
  }

  const timeStr = formatTime12Hour(date)
  const month = date.toLocaleString('default', { month: 'short' })
  const day = date.getDate()

  return `${timeStr} ${month}. ${day}`
}

export function convertToDatetime(date: Date): string {
  return date.toISOString().slice(0, 19) + 'Z'
}

export const getUserTimezone = (defaultTimezone: string): string => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return TIMEZONES.find((tz) => tz.value === userTimezone)?.value || defaultTimezone
}

export function formatCompactDuration(duration: string): string {
  if (!duration) return '—'

  let days = 0,
    hours = 0,
    minutes = 0,
    seconds = 0

  const daysTimeMatch = duration.match(/^(\d+)\s+(\d{2}):(\d{2}):(\d{2})/)
  if (daysTimeMatch) {
    days = parseInt(daysTimeMatch[1])
    hours = parseInt(daysTimeMatch[2])
    minutes = parseInt(daysTimeMatch[3])
    seconds = parseInt(daysTimeMatch[4])
  } else {
    const timeOnlyMatch = duration.match(/^(\d{2}):(\d{2}):(\d{2})/)
    if (timeOnlyMatch) {
      hours = parseInt(timeOnlyMatch[1])
      minutes = parseInt(timeOnlyMatch[2])
      seconds = parseInt(timeOnlyMatch[3])
    } else {
      return duration
    }
  }

  const units: Array<[number, string]> = [
    [days, 'd'],
    [hours, 'h'],
    [minutes, 'm'],
    [seconds, 's'],
  ]

  const nonZero = units.filter(([val]) => val > 0)

  if (nonZero.length === 0) return '< 1s'

  return nonZero
    .slice(0, 2)
    .map(([val, unit]) => `${val}${unit}`)
    .join(' ')
}

export function formatHourOfDay(hour: number): string {
  if (hour === 0) {
    return '12 AM'
  }
  if (hour === 12) {
    return '12 PM'
  }
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`
}
